import Image from "next/image";
import Link from "next/link";
import React, { useEffect, useState } from "react";
import { BiEnvelopeOpen, BiMap, BiPhoneCall } from "react-icons/bi";
import { BsFacebook, BsInstagram, BsLinkedin, BsYoutube } from "react-icons/bs";

import Logo from "../../../public/assets/white_logo.png"; // Adjust path as needed
import { MdOutlineKeyboardDoubleArrowUp } from "react-icons/md";

const Footer = () => {
  const [showScroll, setShowScroll] = useState(false);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const handleScroll = () => setShowScroll(window.scrollY > 200);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <footer
      role="contentinfo"
      aria-labelledby="footer-heading"
      className="bg-[#282930]  text-gray-100 
      lg:pt-2 lg:mt-20 md:mt-10 md:pt-[20px] relative
      before:content-[''] before:absolute lg:before:-top-[170px] md:before:-top-[130px] sm:before:-top-[110px] before:-top-[110px] before:left-0 before:w-full
      before:h-[120px] md:before:h-[150px] lg:before:h-[180px]
      before:bg-[url(../../public/assets/background/footer-bottle-bg.png)]
      before:bg-repeat-x before:bg-bottom before:bg-cover "
    >
      <div className="container mx-auto z-10 relative px-4 md:px-5">
        <h2 id="footer-heading" className="sr-only">
          Footer
        </h2>

        {/* === GRID === */}
        <div className="grid gap-8 sm:grid-cols-2 xl:grid-cols-6 xl:gap-20 mt-8">
          <div className="md:flex hidden flex-col gap-4">
            <Link href="/">
              <Image
                src={Logo}
                alt="Emerge Glass logo"
                height={300}
                width={300}
                loading="lazy"
                className=" w-40 sm:w-48"
              />
            </Link>
            <nav aria-label="Social media">
              <ul role="list" className="flex items-center gap-5 mt-5 mb-5">
                {[
                  {
                    icon: <BsFacebook />,
                    href: "https://www.facebook.com/emergeglassindia",
                    label: "Facebook",
                  },
                  {
                    icon: <BsInstagram />,
                    href: "https://www.instagram.com/emergeglassindia/",
                    label: "Instagram",
                  },
                  {
                    icon: <BsLinkedin />,
                    href: "https://www.linkedin.com/company/emergeglass",
                    label: "LinkedIn",
                  },
                  {
                    icon: <BsYoutube />,
                    href: "https://www.youtube.com/channel/UCtEdeCULV1IWS0EzK3D8qEQ",
                    label: "YouTube",
                  },
                ].map((item) => (
                  <li key={item.label}>
                    <Link
                      href={item.href}
                      target="_blank"
                      aria-label={item.label}
                      className="text-xl text-gray-100 hover:text-red-400  dark:hover:text-red-500 transition-colors"
                    >
                      {item.icon}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* === What We Do === */}
          <div className="md:flex hidden flex-col items-start gap-2 xl:col-span-2">
            <h3 className="text-base font-semibold">What We Do</h3>
            <nav aria-label="Product links">
              <ul role="list" className="flex flex-wrap gap-2 w-full">
                {[
                  { href: "/beer-bottle", text: "Beer Bottle - Amber" },
                  { href: "/beer-bottle", text: "Beer Bottle - Green" },
                  { href: "/food-jar", text: "Food Jar" },
                  { href: "/liquor-bottle", text: "Liquor Bottles" },
                  { href: "/pharma-bottle", text: "Pharma Products" },
                  { href: "/sheet-glass", text: "Sheet Glass" },
                ].map((item) => (
                  <li
                    key={item.text}
                    className="w-[48%] text-gray-400  text-sm hover:text-red-400 dark:hover:text-red-500 transition-colors"
                  >
                    <Link href={item.href}>{item.text}</Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* === About === */}
          <div className="xl:flex hidden flex-col items-start gap-2">
            <h3 className="text-base font-semibold">About Us</h3>
            <ul role="list" className="flex flex-col gap-2">
              {[
                { href: "/infrastructure", text: "Infrastructure" },
                {
                  href: "https://www.emergeglass.in/boardreportmgt17.pdf",
                  text: "Board Report MGT 722-2023",
                },
                {
                  href: "https://www.emergeglass.in/boardreportmgt2024.pdf",
                  text: "Board Report MGT 722-2024",
                },
              ].map((item) => (
                <li
                  key={item.text}
                  className="text-gray-400 text-sm hover:text-red-400 dark:hover:text-red-500 transition-colors"
                >
                  {item.href.startsWith("http") ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {item.text}
                    </a>
                  ) : (
                    <Link href={item.href}>{item.text}</Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* === Contact === */}
          <div className="xl:flex hidden flex-col items-start gap-2 xl:col-span-2">
            <h3 className="text-base font-semibold">Get In Touch</h3>
            <ul role="list" className="flex flex-col gap-4">
              <li>
                <Link
                  href="https://maps.app.goo.gl/7g1Faz5ZtFxpNDABA"
                  target="_blank"
                  className="grid grid-cols-5 text-gray-400  text-sm hover:text-red-400 dark:hover:text-red-500 transition-colors"
                >
                  <BiMap className="mt-1 opacity-40" aria-hidden="true" />
                  <address className="col-span-4 not-italic">
                    15th Floor, Vijaya Building,
                    <br /> Barakhamba Road,
                    <br /> Connaught Place, Delhi 110001
                  </address>
                </Link>
              </li>
              <li>
                <Link
                  href="mailto:info@emergeglass.in"
                  className="grid grid-cols-5 text-gray-400  text-sm hover:text-red-400 dark:hover:text-red-500 transition-colors"
                >
                  <BiEnvelopeOpen
                    className="mt-1 opacity-40"
                    aria-hidden="true"
                  />
                  <span className="col-span-4">info@emergeglass.in</span>
                </Link>
              </li>
              <li>
                <Link
                  href="tel:+911141232400"
                  className="grid grid-cols-5 text-gray-400  text-sm hover:text-red-400 dark:hover:text-red-500 transition-colors"
                >
                  <BiPhoneCall className="mt-1 opacity-40" aria-hidden="true" />
                  <span className="col-span-4">+91 11 4123 2400</span>
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-5  xl:pb-0">
          <hr className="border-gray-700 dark:border-gray-300 hidden lg:block" />
          <p className="text-gray-400  text-xs py-2 text-center">
            ©2026 Emerge Glass | All rights reserved
          </p>
        </div>
      </div>

      <button
        onClick={scrollToTop}
        className={`
          fixed xl:bottom-6 bottom-10 right-6 p-3 cursor-pointer rounded-full bg-white text-red-500 
          transition-all duration-500 ease-in-out transform shadow-2xl border-2 border-red-400
          ${
            showScroll
              ? "opacity-100 scale-100"
              : "opacity-0 scale-75 pointer-events-none"
          }
          hover:bg-red-600 hover:text-white
        `}
        aria-label="Scroll to top"
      >
        <MdOutlineKeyboardDoubleArrowUp size={22} />
      </button>
    </footer>
  );
};

export default Footer;
